import { useEffect, useState } from 'react'
import './ComplianceTab.css'
import { API_BASE as API_BASE_CONFIG } from '../../config'
import { useAuth } from '../../AuthProvider'
import { FINDING_TABS, findingCount, STATUS_LABEL, isClosed, statusOf, tabFindingCounts, isOpenTier, tierOf, groupByCategory } from './findings'
import { FindingActions, FindingStatusLine } from './FindingStatus'
import { fetchClosedFindings } from './api'
import LoadError from '../../components/LoadError'
import EmployeeCell from '../CustomerPortal/Lifeline/EmployeeCell'
import { loadErrorMessage } from '../../utils/loadState'

const DRILL_COL_ORDER = [
  'employee_number', 'first_name', 'last_name',
  'company_code', 'pay_group', 'status',
  'job_code', 'job_title', 'hire_date', 'last_hire_date', 'term_date',
  'salary_or_hourly', 'flsa_status',
]

function sortDrillColumns(cols) {
  if (!cols || !cols.length) return cols
  const ordered = DRILL_COL_ORDER.filter(c => cols.includes(c))
  const rest = cols.filter(c => !DRILL_COL_ORDER.includes(c))
  return [...ordered, ...rest]
}

function fmtColHeader(c) {
  return c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

const API_BASE = API_BASE_CONFIG
const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('xlr8_token') || ''}` })

// Map obs.type to severity
function sevLabel(type) {
  if (type === 'risk') return 'Critical'
  if (type === 'insight') return 'Warning'
  return 'Info'
}
function sevClass(type) {
  if (type === 'risk') return 'critical'
  if (type === 'insight') return 'warning'
  return 'info'
}

// ─── Single finding card ─────────────────────────────────
function FindingCard({ obs, customerId, onOpenEmployee = null }) {
  // the server sends no SQL to a non-admin; the button is admin-only here as well
  const { isAdmin } = useAuth()
  const [expanded, setExpanded] = useState(false)
  const [drillData, setDrillData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [showSQL, setShowSQL] = useState(false)
  const [exporting, setExporting] = useState(false)
  // this finding's status as last saved (FindingActions hands back the updated finding)
  const [cur, setCur] = useState(obs)

  const sev = sevClass(obs.type)
  const count = findingCount(obs)   // subjects, else people, else records -- never a bare "affected"
  const status = statusOf(cur)
  const isResolved = isClosed(cur)

  const handleExpand = async () => {
    if (!obs.id) return
    if (expanded) { setExpanded(false); return }
    setExpanded(true)
    if (drillData !== null) return
    setLoading(true)
    try {
      const res = await fetch(
        `${API_BASE}/api/observations/${customerId}/drilldown/${obs.id}`,
        { headers: authHeaders() }
      )
      if (res.ok) setDrillData(await res.json())
      else setDrillData({ error: `HTTP ${res.status}` })
    } catch (e) {
      setDrillData({ error: e.message })
    }
    setLoading(false)
  }

  const handleExport = async (e) => {
    e.stopPropagation()
    if (!drillData?.rows?.length) return
    setExporting(true)
    const cols = sortDrillColumns(drillData.columns || Object.keys(drillData.rows[0]))
    const csv = [cols.join(','), ...drillData.rows.map(r =>
      cols.map(c => { const v = r[c] != null ? String(r[c]) : ''; return v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v }).join(',')
    )].join('\n')
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
      download: `compliance_${obs.id?.slice(0,8)}.csv`
    })
    a.click()
    setExporting(false)
  }

  return (
    <div className={`ct-card ct-card-${sev} ${expanded ? 'expanded' : ''} ${isResolved ? 'ct-card-resolved' : ''}`}>
      <div className="ct-card-top" onClick={handleExpand}>
        <div className={`ct-sev-bar ct-sev-${sev}`} />
        <div className="ct-card-main">
          <div className="ct-card-tags">
            <span className={`ct-sev-tag ct-sev-tag-${isResolved ? 'resolved' : sev}`}>
              {isResolved ? STATUS_LABEL[status] : sevLabel(obs.type)}
            </span>
            {!isResolved && status !== 'open' && (
              <span className={`fs-pill fs-pill-${status} fs-pill-inline`}>{STATUS_LABEL[status]}</span>
            )}
          </div>
          <div className="ct-card-body">
            <div className={`ct-card-title ${isResolved ? 'ct-title-dim' : ''}`}>{obs.title}</div>
            <div className="ct-card-detail">{obs.detail}</div>
            {obs.source && (
              <div className="ct-card-meta">
                <span className="ct-source-badge">{obs.source}</span>
              </div>
            )}
          </div>
        </div>
        <div className="ct-card-right">
          {count && (
            <div>
              <div className={`ct-count ct-count-${sev}`}>{count.value.toLocaleString()}</div>
              <div className="ct-count-label">{count.label}</div>
            </div>
          )}
          {obs.id && !isResolved && (
            <div className="ct-card-actions">
              <button className={`ct-action-btn ct-action-${sev}`} onClick={e => { e.stopPropagation(); handleExpand() }}>
                View →
              </button>
            </div>
          )}
          {obs.id && <span className={`ct-expand-icon ${expanded ? 'open' : ''}`}>▾</span>}
        </div>
      </div>

      {expanded && (
        <div className="ct-drill" onClick={e => e.stopPropagation()}>

          {/* Resolution workflow. Only a scan closes a finding ("Verified & Closed"):
              people acknowledge it and say a fix was submitted; the next scan on newer
              data checks the fix. Which buttons show comes from the viewer's permissions
              (FindingActions); the server enforces the same rules. */}
          {obs.id && (
            <div className="ct-resolution">
              <div className="ct-res-label">Resolution Workflow</div>
              {status !== 'dismissed' && (
              <div className="ct-res-steps">
                <div className="ct-res-step ct-rs-done">
                  <span className="ct-rs-num">✓</span>Detected
                </div>
                <div className={`ct-res-step ${status === 'open' || status === 'acknowledged' ? 'ct-rs-current' : 'ct-rs-done'}`}>
                  <span className="ct-rs-num">2</span>Under Review
                </div>
                <div className={`ct-res-step ${status === 'fix_submitted' ? 'ct-rs-current' : status === 'resolved' ? 'ct-rs-done' : ''}`}>
                  <span className="ct-rs-num">3</span>Fix Submitted
                </div>
                <div className={`ct-res-step ${status === 'resolved' ? 'ct-rs-done' : ''}`}>
                  <span className="ct-rs-num">4</span>Verified &amp; Closed
                </div>
              </div>
              )}
              <FindingStatusLine finding={cur} />
              <FindingActions finding={cur} onChange={setCur} prefix="ct" />
            </div>
          )}

          {loading && <div className="ct-drill-loading">Loading data…</div>}
          {drillData?.error && <div className="ct-drill-error">Failed to load: {drillData.error}</div>}
          {drillData?.rows?.length > 0 && (
            <>
              <div className="ct-drill-header">
                <span className="ct-drill-title">
                  {drillData.total_rows?.toLocaleString() || drillData.rows.length} affected records
                </span>
                <div className="ct-drill-header-right">
                  {isAdmin && drillData.sql && (
                    <button className="ct-sql-btn" onClick={() => setShowSQL(s => !s)}>
                      {showSQL ? 'Hide SQL' : 'SQL ▾'}
                    </button>
                  )}
                  <button className="ct-export-btn" onClick={handleExport} disabled={exporting}>
                    ↓ Export CSV
                  </button>
                </div>
              </div>
              {isAdmin && showSQL && drillData.sql && (
                <div className="ct-sql-block"><pre>{drillData.sql}</pre></div>
              )}
              <div className="ct-table-wrap">
                <table className="ct-table">
                  <thead>
                    <tr>{sortDrillColumns(drillData.columns).map(c => <th key={c}>{fmtColHeader(c)}</th>)}</tr>
                  </thead>
                  <tbody>
                    {drillData.rows.slice(0, 500).map((row, i) => (
                      <tr key={i}>
                        {sortDrillColumns(drillData.columns).map(c => <td key={c}>{c === 'employee_number' ? <EmployeeCell number={row[c]} employeeRef={drillData.employee_refs?.[`${row.company_code ?? ''}|${row[c]}`]} onOpenEmployee={onOpenEmployee} /> : (row[c] != null ? String(row[c]) : '—')}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {drillData.total_rows > 500 && (
                <div className="ct-drill-more">
                  Showing 500 of {drillData.total_rows.toLocaleString()} rows
                </div>
              )}
            </>
          )}
          {drillData && !drillData.error && (!drillData.rows || drillData.rows.length === 0) && (
            <div className="ct-drill-note">{drillData.note || 'No row-level data available for this check.'}</div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Domain section ──────────────────────────────────────
// One group per check-library category; the heading is the library's category as written.
function DomainSection({ category, findings, customerId, onOpenEmployee = null }) {
  const [collapsed, setCollapsed] = useState(false)
  const critCount = findings.filter(f => f.type === 'risk').length
  const warnCount = findings.filter(f => f.type === 'insight').length

  return (
    <div className="ct-domain">
      <div className="ct-domain-header" onClick={() => setCollapsed(c => !c)}>
        <div className="ct-domain-left">
          <span className="ct-domain-title">{category}</span>
          <div className="ct-domain-counts">
            {critCount > 0 && <span className="ct-domain-count ct-dc-critical">{critCount} critical</span>}
            {warnCount > 0 && <span className="ct-domain-count ct-dc-warning">{warnCount} warning</span>}
            {critCount === 0 && warnCount === 0 && (
              <span className="ct-domain-count ct-dc-info">{findings.length} info</span>
            )}
          </div>
        </div>
        <span className={`ct-domain-toggle ${collapsed ? '' : 'open'}`}>▾</span>
      </div>
      {!collapsed && (
        <div className="ct-domain-body">
          {findings.map((f, i) => (
            <FindingCard key={f.id || i} obs={f} customerId={customerId} onOpenEmployee={onOpenEmployee} />
          ))}
        </div>
      )}
    </div>
  )
}

// No "passing checks" list: nothing in the payload says which individual checks ran clean,
// and a list of checks that are not backed by a result is invented. Add one only from real
// per-check results.

// ═════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════
export default function ComplianceTab({ customerId, observations, onOpenEmployee = null }) {
  const [severityFilter, setSeverityFilter] = useState('open')
  // The profile carries the ACTIVE findings (open, acknowledged, fix submitted); resolved
  // and dismissed ones come from the findings list, read once per customer.
  const [closed, setClosed] = useState([])
  // A failed read is shown as a failure with a Retry, never as "0 resolved" (item 201).
  const [closedError, setClosedError] = useState(null)
  const [closedTry, setClosedTry] = useState(0)
  useEffect(() => {
    let live = true
    setClosedError(null)
    if (customerId) fetchClosedFindings(customerId)
      .then(rows => { if (live) setClosed(rows) })
      .catch(err => { if (live) { setClosed([]); setClosedError(loadErrorMessage(err)) } })
    return () => { live = false }
  }, [customerId, closedTry])
  const closedLoadError = closedError && (
    <LoadError inline what="resolved and dismissed findings" error={closedError} onRetry={() => setClosedTry(n => n + 1)} />
  )

  // Only findings that carry a compliance rule code; the rest are on the Health tab.
  const complianceObs = (observations || []).filter(FINDING_TABS.compliance)
  const activeIds = new Set(complianceObs.map(o => o.id))
  const closedCompliance = closed.filter(o => FINDING_TABS.compliance(o) && !activeIds.has(o.id))
  // this tab's numbers -- the same function the tab-bar badge reads (findings.tabFindingCounts)
  const { critical: critCount, warning: warnCount, info: infoCount, open: openCount, active: activeObs } =
    tabFindingCounts(observations, 'compliance')

  // Apply severity filter
  const filtered = (severityFilter === 'resolved' || severityFilter === 'dismissed'
    ? closedCompliance.filter(o => statusOf(o) === severityFilter)
    : activeObs.filter(o => {
        if (severityFilter === 'open') return isOpenTier(o)
        // the tiers the counts use (findings.tierOf): an FYI finding is in none of them
        if (['critical', 'warning', 'info'].includes(severityFilter)) return tierOf(o) === severityFilter
        return true
      }))

  // Grouped by each finding's check-library category (findings.groupByCategory):
  // alphabetical, Uncategorized last -- the same grouping as the Health tab.
  const groups = groupByCategory(filtered)

  const resolvedCount = closedCompliance.filter(o => statusOf(o) === 'resolved').length
  const dismissedCount = closedCompliance.filter(o => statusOf(o) === 'dismissed').length
  const totalFindings = complianceObs.filter(o => tierOf(o) !== 'fyi').length   // FYI findings are never counted

  if (!observations?.length && !closed.length) {
    if (closedError) return <div className="ct-root">{closedLoadError}</div>
    return (
      <div className="ct-empty">
        <div className="ct-empty-icon">⚖</div>
        <div className="ct-empty-title">No compliance data yet</div>
        <div className="ct-empty-sub">Run an analysis to surface compliance findings for this customer.</div>
      </div>
    )
  }

  return (
    <div className="ct-root">
      {/* Summary bar */}
      <div className="ct-summary">
        <div className="ct-sum-stat">
          <div className={`ct-sum-val ${critCount > 0 ? 'critical' : 'clear'}`}>{critCount}</div>
          <div className="ct-sum-label">Critical</div>
          <div className="ct-sum-sub">Immediate action required</div>
        </div>
        <div className="ct-sum-stat">
          <div className={`ct-sum-val ${warnCount > 0 ? 'warning' : 'clear'}`}>{warnCount}</div>
          <div className="ct-sum-label">Warning</div>
          <div className="ct-sum-sub">Review within 30 days</div>
        </div>
        <div className="ct-sum-stat">
          <div className="ct-sum-val info">{infoCount}</div>
          <div className="ct-sum-label">Info</div>
          <div className="ct-sum-sub">Monitor / review</div>
        </div>
        <div className="ct-sum-stat">
          <div className="ct-sum-val resolved">{closedError ? '—' : resolvedCount}</div>
          <div className="ct-sum-label">Resolved</div>
          <div className="ct-sum-sub">Closed by a scan</div>
        </div>
        <div className="ct-sum-stat">
          <div className="ct-sum-val total">{totalFindings}</div>
          <div className="ct-sum-label">Findings</div>
          <div className="ct-sum-sub">Checks with results</div>
        </div>
      </div>

      {closedLoadError}

      {/* Filter bar */}
      <div className="ct-filter-bar">
        {[
          { key: 'open',     label: `Open ${openCount}` },
          { key: 'critical', label: `🔴 Critical ${critCount}` },
          { key: 'warning',  label: `🟡 Warning ${warnCount}` },
          { key: 'info',     label: `🔵 Info ${infoCount}` },
          { key: 'resolved', label: `✓ Resolved ${resolvedCount}` },
          { key: 'dismissed', label: `Dismissed ${dismissedCount}` },
        ].map(f => (
          <button
            key={f.key}
            className={`ct-filter-chip ${severityFilter === f.key ? 'active ct-fc-' + f.key : ''}`}
            onClick={() => setSeverityFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Domain sections */}
      {groups.length === 0 && (
        <div className="ct-no-results">
          No {severityFilter} findings for this customer.
        </div>
      )}

      {groups.map(([category, findings]) => (
        <DomainSection
          key={category}
          category={category}
          findings={findings}
          customerId={customerId}
          onOpenEmployee={onOpenEmployee}
        />
      ))}
    </div>
  )
}

